/**
 * Error Handling Utilities
 * Provides consistent error handling across the application
 */

import { showToast } from './toast';

/**
 * Handle API errors consistently
 * @param {Error} error - The error object from API call
 * @param {string} context - Context message (e.g., "Failed to load users")
 * @param {boolean} showErrorToast - Whether to show error toast (default: true)
 * @returns {string} - User-friendly error message
 */
export const handleAPIError = (error, context = 'Operation failed', showErrorToast = true) => {
  let errorMessage = context;
  
  // Extract message from enhanced error
  if (error.message && typeof error.message === 'string') {
    errorMessage = `${context}: ${error.message}`;
  } else if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    errorMessage = `${context}: ${Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : detail}`;
  } else if (error.response?.data?.message) {
    errorMessage = `${context}: ${error.response.data.message}`;
  }
  
  // Show toast notification
  if (showErrorToast) {
    showToast.error(errorMessage);
  }
  
  // Log error for debugging
  console.error(context, error);
  
  return errorMessage;
};

/**
 * Handle API success messages
 * @param {string} message - Success message
 * @param {boolean} showToast - Whether to show success toast (default: true)
 */
export const handleAPISuccess = (message, shouldShowToast = true) => {
  if (shouldShowToast) {
    showToast.success(message); 
  }
};

/**
 * Wrapper for async operations with error handling
 * @param {Function} asyncFunction - Async function to execute
 * @param {Object} options - Options object
 * @param {string} options.onError - Error context message
 * @param {string} options.onSuccess - Success message
 * @param {Function} options.onFinally - Finally callback
 * @returns {Promise} - Promise with handled errors
 */
export const withErrorHandling = async (
  asyncFunction,
  options = {}
) => {
  const {
    onError = 'Operation failed',
    onSuccess = null,
    onFinally = null
  } = options;
  
  try {
    const result = await asyncFunction();
    
    // Show success message if provided
    if (onSuccess) {
      handleAPISuccess(onSuccess);
    }
    
    return { success: true, data: result };
  } catch (error) {
    const errorMessage = handleAPIError(error, onError);
    return { success: false, error: errorMessage, data: null };
  } finally {
    if (onFinally) {
      onFinally();
    }
  }
};

export default {
  handleAPIError,
  handleAPISuccess,
  withErrorHandling
};

